import { Alert, Button, Col, InputNumber, Row, Select } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { ID } from '@/types/common'
import { User } from '@/pages/system/user/types'
import {
  BILLING_MODE_OPTIONS,
  CLASS_TYPE_OPTIONS,
} from '../constants'
import { GenerateTemplateItem } from '../page-types'
import { BillingMode, ClassType } from '../types'

type ConfigTemplateEditorProps = {
  templates: GenerateTemplateItem[]
  isAllDayPricing: boolean
  teachers: User[]
  teachersLoading: boolean
  onTemplateChange: (
    localId: string,
    next: Partial<GenerateTemplateItem>,
  ) => void
  onAddTemplate: () => void
  onRemoveTemplate: (localId: string) => void
}

export function ConfigTemplateEditor({
  templates,
  isAllDayPricing,
  teachers,
  teachersLoading,
  onTemplateChange,
  onAddTemplate,
  onRemoveTemplate,
}: ConfigTemplateEditorProps) {
  return (
    <div className="space-y-3">
      {isAllDayPricing ? (
        <Alert
          type="info"
          showIcon
          message="已启用全天班计费"
          description="当前配置下，所有模板都会按全天班处理，模板价格会被忽略并保持为空。"
        />
      ) : null}
      {templates.map((template, index) => (
        <div
          key={template.localId}
          className="rounded-md border border-gray-100 bg-gray-50 p-3"
        >
          <div className="mb-3 text-sm text-gray-500">模板 {index + 1}</div>
          <Row gutter={[10, 10]} align="middle">
            <Col xs={24} md={5}>
              <Select
                style={{ width: '100%' }}
                value={template.classType}
                showSearch={false}
                onChange={(value: ClassType) =>
                  onTemplateChange(template.localId, { classType: value })
                }
              >
                {CLASS_TYPE_OPTIONS.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={5}>
              <Select
                style={{ width: '100%' }}
                value={isAllDayPricing ? BillingMode.ALL_DAY : template.billingMode}
                showSearch={false}
                disabled={isAllDayPricing}
                onChange={(value: BillingMode) =>
                  onTemplateChange(template.localId, { billingMode: value })
                }
              >
                {BILLING_MODE_OPTIONS.filter(
                  (item) => isAllDayPricing || item.value !== BillingMode.ALL_DAY,
                ).map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={5}>
              <Select
                style={{ width: '100%' }}
                value={template.teacherId}
                showSearch={false}
                placeholder={
                  template.classType === ClassType.GROUP
                    ? '老师（集体课可不选）'
                    : '老师'
                }
                loading={teachersLoading}
                allowClear={template.classType === ClassType.GROUP}
                onChange={(value: ID | undefined) =>
                  onTemplateChange(template.localId, { teacherId: value })
                }
              >
                {teachers.map((teacher) => (
                  <Select.Option key={teacher.id} value={teacher.id}>
                    {teacher.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={6}>
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                precision={2}
                value={isAllDayPricing ? undefined : template.coursePrice}
                placeholder={
                  isAllDayPricing
                    ? '全天班统一费用在上方设置'
                    : template.billingMode === BillingMode.GIFT
                      ? '赠课不可填'
                      : template.billingMode === BillingMode.ALL_DAY
                        ? '全天不可填'
                        : template.billingMode === BillingMode.MONTHLY
                          ? '模板月费'
                          : '单价'
                }
                disabled={
                  isAllDayPricing ||
                  template.billingMode === BillingMode.GIFT ||
                  template.billingMode === BillingMode.ALL_DAY
                }
                onChange={(value) =>
                  onTemplateChange(template.localId, {
                    coursePrice: value ?? undefined,
                  })
                }
              />
            </Col>
            <Col xs={12} md={3}>
              <Button
                className="w-full"
                danger
                disabled={templates.length <= 1}
                onClick={() => onRemoveTemplate(template.localId)}
              >
                删除
              </Button>
            </Col>
          </Row>
        </div>
      ))}
      <Button block icon={<PlusOutlined />} onClick={onAddTemplate}>
        新增模板
      </Button>
    </div>
  )
}
